import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { applyTheme, readStoredTheme, resolveTheme, storeTheme, systemPrefersDark, type Theme } from './theme';
import { ThemeContext } from './themeContext';

/**
 * Light / dark display mode for every surface (public site, portals, maps,
 * 3D viewer). Until the visitor chooses, the system setting is followed —
 * including live changes to it.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [explicit, setExplicit] = useState<Theme | null>(() => readStoredTheme());
  const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark());
  const theme = resolveTheme(explicit, prefersDark);

  useEffect(() => applyTheme(theme), [theme]);

  useEffect(() => {
    if (explicit) return;
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [explicit]);

  const setTheme = useCallback((next: Theme) => {
    storeTheme(next);
    setExplicit(next);
  }, []);
  const toggleTheme = useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [setTheme, theme]);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
